import { useFindCrItems } from '@/components/CrItemsSelect/Provider';
import { useFindCrSubItems } from '@/components/CrSubItemsSelect/Provider';
import { useFindCurrencies } from '@/components/CurrenciesSelect/Provider';
import { useFindDepts } from '@/components/DeptSelect/Provider';
import { useFindExcise } from '@/components/ExciseSelect/Provider';
import { useFindExItems } from '@/components/ExItemSelect/Provider';
import { useFindProjectCodes } from '@/components/ProjectSelect/Provider';
import { useGetExcisesAllSuspense } from '@/context/services/excise/Excise.service';
import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { canEditExciseValue } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/Cells/ExciseValueCell';
import { InvoiceTransactionHelper } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/hooks';
import { useFindPulldownOption } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Components/CustomTransactionItems/PulldownType/useFindPulldownOption';
import { ConvertInvoiceTransaction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/model';
import {
  InvoiceTransaction,
  TransactionProperty,
} from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import { toFloat } from '@/utils/convert/toNumber';
import { UploadFile, randomId } from '@moneyforward/ap-frontend-components';
import {
  NotFoundRecord,
  UnMatchHeader,
  importExcel,
  parse,
} from '@moneyforward/csv-excel-imp-exp-util';
import { stringToDayjs } from 'date-util';
import { Dayjs } from 'dayjs';
import { useCallback } from 'react';
import {
  CustomInvoiceTransactionMapping,
  importMappings,
} from '../importExportMapping';
type ImportArgs = {
  file: UploadFile | undefined;
};

export { NotFoundRecord, UnMatchHeader };

type BuildCustomTransactionItemsParams = {
  invoiceTransaction: CustomInvoiceTransactionMapping;
  transactionProperties: TransactionProperty[];
  findPulldownOptionByLabel: (
    propertyId: string,
    label: string
  ) => Promise<string>;
};

export const buildCustomTransactionItems = async ({
  invoiceTransaction,
  transactionProperties,
  findPulldownOptionByLabel,
}: BuildCustomTransactionItemsParams): Promise<
  Record<string, string | Dayjs | null | undefined>
> => {
  const masterKeys = new Set([
    'dealDate',
    'name',
    'exItemName',
    'unitPriceExcludingTax',
    'unitPriceIncludingTax',
    'quantity',
    'totalValueExcludingTax',
    'totalValueIncludingTax',
    'exciseName',
    'tax',
    'withholdingIncomeTax',
    'memo',
    'deptName',
    'projectName',
    'crItemName',
    'crSubItemName',
    'currency',
    'jpyRate',
    'invoiceKind',
  ]);

  const filteredTransactionProperties = transactionProperties.filter(
    (prop) => prop.type === 'custom'
  );
  const customTransactionKeys = Object.fromEntries(
    Object.entries(invoiceTransaction).filter(([key]) => !masterKeys.has(key))
  );

  const entries = await Promise.all(
    Object.entries(customTransactionKeys).map(async ([, value], index) => {
      const transactionProperty = filteredTransactionProperties[index];

      if (!transactionProperty) return null;

      if (transactionProperty.componentType === 'pulldown') {
        const pulldownOptionId = await findPulldownOptionByLabel(
          transactionProperty.id,
          value
        );
        return [transactionProperty.id, pulldownOptionId] as const;
      }

      if (transactionProperty.componentType === 'date') {
        return [transactionProperty.id, stringToDayjs(value)] as const;
      }

      return [transactionProperty.id, value] as const;
    })
  );

  return entries.reduce((acc, entry) => {
    if (entry) {
      acc[entry[0]] = entry[1];
    }
    return acc;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  }, {} as Record<string, string | Dayjs | null | undefined>);
};

const shouldUseJPY = (defaultValue: string) =>
  defaultValue === null || defaultValue === undefined || defaultValue === '';

const getCurrencyAndRate = (defaultCurrency: string, defaultRate: string) => ({
  currency: shouldUseJPY(defaultCurrency) ? 'JPY' : defaultCurrency,
  jpyRate: shouldUseJPY(defaultRate) ? 1 : toFloat(defaultRate),
});

const useConvertInvoiceTransactionMappingToInvoiceTransaction = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  hasWithholdingIncomeTax: boolean,
  transactionProperties: TransactionProperty[] = [],
  updateExciseValue: InvoiceTransactionHelper['updateExciseValue']
) => {
  const { findExciseByName } = useFindExcise();
  const { findDeptByName } = useFindDepts();
  const { findCrItemsByName } = useFindCrItems();
  const { findCrSubItemsByName } = useFindCrSubItems();
  const { findExItemsByName } = useFindExItems();
  const { findProjectCodesByName } = useFindProjectCodes();
  const { findCurrenciesByName } = useFindCurrencies();
  const { findPulldownOptionByLabel } = useFindPulldownOption();

  const drExciseList = useGetExcisesAllSuspense();

  return useCallback(
    async (
      data: CustomInvoiceTransactionMapping[]
    ): Promise<InvoiceTransaction[]> => {
      return Promise.all(
        data.map(async (item): Promise<InvoiceTransaction> => {
          const customTransactionItems = await buildCustomTransactionItems({
            invoiceTransaction: item,
            transactionProperties,
            findPulldownOptionByLabel,
          });
          const excise = findExciseByName(item.exciseName);
          const dept = findDeptByName(item.deptName);
          const crItem = findCrItemsByName(item.crItemName);
          const crSubItem = findCrSubItemsByName(item.crSubItemName, crItem);
          const exItem = findExItemsByName(item.exItemName);
          const project = findProjectCodesByName(item.projectName);
          const currency = findCurrenciesByName(item.currency);
          const taxValue = toFloat(item.tax);
          const isTaxEmpty = !item.tax || item.tax === '' || taxValue === 0;
          const hasExciseName = !!item.exciseName;

          const canCalculateExciseValue = canEditExciseValue(
            excise,
            drExciseList
          );

          const shouldCalculateExciseValue =
            isTaxEmpty && hasExciseName && canCalculateExciseValue;

          const { currency: _currency, jpyRate: _jpyRate } = getCurrencyAndRate(
            currency,
            item.jpyRate
          );

          const value = {
            id: 'imp' + String(Math.floor(Math.random() * 10000)),
            number: INVOICE_NUMBER_UNSAVED,
            dealDate: stringToDayjs(item.dealDate) ?? null,
            name: item.name,
            exItemId: exItem,
            exItemName: item.exItemName,
            unit: toFloat(item.unitPriceExcludingTax),
            taxIncludedUnit: toFloat(item.unitPriceIncludingTax),
            quantity: toFloat(item.quantity),
            totalValue: toFloat(item.totalValueExcludingTax),
            taxIncludedTotalValue: toFloat(item.totalValueIncludingTax),
            drExciseId: excise,
            drExciseName: item.exciseName,
            invoiceKind: item.invoiceKind ? item.invoiceKind : '',
            exciseValue: toFloat(item.tax),
            withholdingIncomeTax: toFloat(item.withholdingIncomeTax),
            hasWithholdingIncomeTax: !hasWithholdingIncomeTax
              ? false
              : !!item.withholdingIncomeTax,
            memo: item.memo,
            deptId: dept,
            deptName: item.deptName,
            projectCodeId: project,
            projectCodeName: item.projectName,
            currency: _currency,
            jpyRate: _jpyRate,
            crItemId: crItem,
            crItemName: item.crItemName,
            crSubItemId: crSubItem,
            crSubItemName: item.crSubItemName,
            rowKey: randomId('invoice-transaction', 8),
            custom_transaction_items: customTransactionItems,
          } satisfies Omit<InvoiceTransaction, 'isCurrencyJPY'>;
          const invoice = ConvertInvoiceTransaction(value);
          let exciseValue = value.exciseValue;
          if (shouldCalculateExciseValue) {
            const updatedInvoice = updateExciseValue(invoice);
            exciseValue = updatedInvoice.exciseValue ?? 0;
          }
          if (!invoiceTransactionDetails) {
            invoice.quantity = 1;
            const totalValue = taxInclude
              ? value.taxIncludedTotalValue - exciseValue
              : value.totalValue;
            invoice.unit = totalValue;
            invoice.taxIncludedUnit = totalValue;
          }
          // NOTE: 税込場合 数,単 の入力は受け付けない
          if (taxInclude) {
            // 数量, 単価(税込), 単価(税抜)
            invoice.quantity = 1;
            invoice.taxIncludedUnit = value.taxIncludedTotalValue;
            const totalValue = value.taxIncludedTotalValue - exciseValue; // 税込金額 - 税額
            invoice.unit = totalValue;
            invoice.totalValue = totalValue;
          }

          return invoice;
        })
      );
    },
    [
      transactionProperties,
      findPulldownOptionByLabel,
      findExciseByName,
      findDeptByName,
      findCrItemsByName,
      findCrSubItemsByName,
      findExItemsByName,
      findProjectCodesByName,
      findCurrenciesByName,
      drExciseList,
      hasWithholdingIncomeTax,
      invoiceTransactionDetails,
      taxInclude,
      updateExciseValue,
    ]
  );
};

export const useImport = (
  taxInclude: boolean,
  invoiceTransactionDetails: boolean,
  isAdminOrApproval: boolean,
  hasWithholdingIncomeTax: boolean,
  canDisplayDrExcise: boolean,
  transactionProperties: TransactionProperty[] = [],
  updateExciseValue: InvoiceTransactionHelper['updateExciseValue']
) => {
  const convertInvoiceTransaction =
    useConvertInvoiceTransactionMappingToInvoiceTransaction(
      taxInclude,
      invoiceTransactionDetails,
      hasWithholdingIncomeTax,
      transactionProperties,
      updateExciseValue
    );
  const { t } = useTranslation();
  const importInvoiceTransaction = useCallback(
    async ({ file }: ImportArgs): Promise<InvoiceTransaction[]> => {
      return new Promise((resolve, reject) => {
        if (!file) {
          resolve([]);
          return;
        }
        try {
          const isCsv = file.type === 'text/csv';
          const parseFn = isCsv
            ? parse<CustomInvoiceTransactionMapping>
            : importExcel<CustomInvoiceTransactionMapping>;
          const reader: FileReader = new FileReader();
          reader.onloadend = async (e) => {
            try {
              if (!e.target?.result) {
                return resolve([]);
              }
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              const input = Buffer.from(e.target.result as ArrayBuffer);
              const data: CustomInvoiceTransactionMapping[] = await parseFn(
                // @ts-ignore
                input,
                importMappings(
                  isAdminOrApproval,
                  invoiceTransactionDetails,
                  taxInclude,
                  hasWithholdingIncomeTax,
                  canDisplayDrExcise,
                  transactionProperties,
                  t
                )
              );

              resolve(convertInvoiceTransaction(data));
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = (e) => {
            reject(e);
          };
          if (file.originFileObj) {
            const blob = new Blob([file.originFileObj]);
            reader.readAsArrayBuffer(blob);
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [
      convertInvoiceTransaction,
      hasWithholdingIncomeTax,
      invoiceTransactionDetails,
      isAdminOrApproval,
      taxInclude,
      canDisplayDrExcise,
      transactionProperties,
      t,
    ]
  );
  return { importInvoiceTransaction };
};
